import { useState } from 'react';

import { DragEndEvent, DragStartEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';

import { ItemTypes } from './common';

/**
 * Shared @dnd-kit wiring for the column→field mapping cards. Tracks the header
 * pill currently being dragged so the caller can render a DragOverlay preview
 * that follows the cursor, and routes a completed drop to `onMap`.
 *
 * Extracted so the legacy MapRequiredFieldsCard and the Tapestry mapping gate
 * stay in lockstep: the original ENG-2871 overlay fix landed in only one of the
 * two hand-copied DndContexts, leaving Tapestry drops with no floating preview
 * and flaky drop-zone highlighting.
 */
export const useHeaderMappingDnd = (onMap: (fieldId: string, header: string) => void) => {
  const sensors = useSensors(useSensor(PointerSensor));
  // Column header currently being dragged, so the DragOverlay can render a
  // preview that follows the cursor (@dnd-kit draws nothing on its own).
  const [activeHeader, setActiveHeader] = useState<string | null>(null);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    if (active.data.current?.type === ItemTypes.HEADER) {
      setActiveHeader((active.data.current?.label as string) ?? (active.id as string));
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.data.current?.type === ItemTypes.HEADER) {
      onMap(over.id as string, active.id as string);
    }
    setActiveHeader(null);
  };

  const handleDragCancel = () => setActiveHeader(null);

  return { sensors, activeHeader, handleDragStart, handleDragEnd, handleDragCancel };
};
